import { useEffect, useState } from 'react';

/**
 * Own the lifetime of an object URL for a `Blob` (E-SF18).
 *
 * ── WHY THIS IS A HOOK AND NOT A LINE IN THE COMPONENT ─────────────────────
 * `URL.createObjectURL` allocates a document-scoped resource that lives until it
 * is explicitly revoked — it is not garbage-collected with the blob. Minting one
 * inline in a render body would leak a fresh copy of a multi-page PDF on every
 * re-render, for the lifetime of the tab; minting one inside a TanStack `queryFn`
 * would be worse, because a cache entry has no destructor to revoke it from. So
 * the QUERY owns the immutable bytes and THIS owns the URL. One resource, one
 * owner, one revoke.
 *
 * Revokes on unmount and whenever the blob changes (a retry, a different plan), so
 * the previous document's URL cannot outlive the document it pointed at.
 *
 * Returns `null` while there is no blob, which is the honest "nothing to show
 * yet" — never an empty string, which a browser would happily treat as a URL and
 * render as the current page inside the viewer frame.
 *
 * ⚠️ THE CALLER MUST PASS A REFERENTIALLY STABLE BLOB. `new Blob(...)` yields a
 * fresh identity every call, so a blob CONSTRUCTED IN A RENDER BODY changes this
 * effect's dependency on every pass: mint → setState → re-render → new blob →
 * mint again, without end. Writing a test that way took the vitest worker down,
 * which is the cheap version of the same lesson. The production caller is safe
 * because the bytes come from the TanStack cache and are stable for the life of
 * the query entry — that is part of why the query holds the blob rather than the
 * URL (`agreement.queries.ts`).
 */
export function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (blob === undefined) {
      setUrl(null);
      return undefined;
    }
    // Guarded: jsdom (and any non-browser host) may not implement the API. A
    // missing object-URL implementation must degrade to "no inline viewer" — the
    // accessible text alternative and the open-in-new-tab affordance still work —
    // rather than throwing during render.
    if (typeof URL.createObjectURL !== 'function') {
      setUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => {
      // Guarded symmetrically with the mint above. A host that can create object
      // URLs normally revokes them too, but the teardown path must never be the
      // thing that throws — an exception inside a cleanup function propagates out
      // of React's unmount and takes the surrounding teardown with it, turning a
      // missing API into a crash on the way OFF the page.
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(next);
    };
  }, [blob]);

  return url;
}
